import React, { PureComponent } from 'react'
import { arrayOf, bool, shape, string, number } from 'prop-types'
import { withTheme, ThemeProvider } from '@ycos/fela'
import Block from '@ycos/component-block'
import { Grid } from '@ycos/component-grid'
import { hasTag } from '@ycos/coremedia-renderer'
import { Hero } from './hero'
import { LoyaltyHeadingComponent } from './EIP'
import { NewLoyaltyHeadingComponent } from './loyalty'

const layoutHeightMap = {
  'isc-hero': { desktop: '270px', mobile: '270px' },
  'isc-hero-mini': { desktop: '270px', mobile: '270px' },
  'isc-hero-large': { desktop: '540px', mobile: '400px' },
  'isc-hero-title-banner': { desktop: '56.25vw', mobile: '133.3333vw' },
  'isc-hero-full-screen': { desktop: '100vh', mobile: '100vh' },
  'isc-hero-EIP': { desktop: '400px', mobile: '270px', xlDesktop: '400px' },
  'isc-hero-full-screen-large-text': { desktop: '100vh', mobile: '100vh' },
  'isc-hero-loyalty': { desktop: '400px', mobile: '307px', xlDesktop: '400px' },
}

const calculateGridOffset = (centerTextAlignment, direction) => {
  if (centerTextAlignment) {
    return { small: { offset: 0, width: 6 }, medium: { offset: 0, width: 12 } }
  }

  if (direction === 'rtl') {
    return { small: { offset: 1, width: 5 }, medium: { offset: 4, width: 7 } }
  }

  return { small: { offset: 0, width: 5 }, medium: { offset: 1, width: 7 } }
}

const getEmptyPadding = () => {
  return {
    'isc-hero': { desktopBottomPadding: undefined, mobileBottomPadding: undefined },
    'isc-hero-mini': { desktopBottomPadding: undefined, mobileBottomPadding: undefined },
    'isc-hero-large': { desktopBottomPadding: undefined, mobileBottomPadding: undefined },
    'isc-hero-title-banner': { desktopBottomPadding: undefined, mobileBottomPadding: undefined },
    'isc-hero-full-screen': { desktopBottomPadding: undefined, mobileBottomPadding: undefined },
    'isc-hero-EIP': { desktopBottomPadding: undefined, mobileBottomPadding: undefined },
    'isc-hero-full-screen-large-text': { desktopBottomPadding: undefined, mobileBottomPadding: undefined },
    'isc-hero-loyalty': { desktopBottomPadding: undefined, mobileBottomPadding: undefined },
  }
}

const getCenterTextAlignmentPadding = (theme) => {
  return {
    'isc-hero': { desktopBottomPadding: `calc(50% - ${5 * theme.spacingMultiplier}px)`, mobileBottomPadding: `calc(50% - ${5 * theme.spacingMultiplier}px)` },
    'isc-hero-mini': {
      desktopBottomPadding: `calc(50% - ${5 * theme.spacingMultiplier}px)`,
      mobileBottomPadding: `calc(50% - ${5 * theme.spacingMultiplier}px)`
    },
    'isc-hero-large': {
      desktopBottomPadding: `calc(50% - ${6 * theme.spacingMultiplier}px)`,
      mobileBottomPadding: `calc(50% - ${4 * theme.spacingMultiplier}px)`
    },
    'isc-hero-title-banner': {
      desktopBottomPadding: `calc(50% - ${6 * theme.spacingMultiplier}px)`,
      mobileBottomPadding: `calc(50% - ${4 * theme.spacingMultiplier}px)`
    },
    'isc-hero-full-screen': {
      desktopBottomPadding: `calc(50% - ${6 * theme.spacingMultiplier}px)`,
      mobileBottomPadding: `calc(50% - ${4 * theme.spacingMultiplier}px)`
    },
    'isc-hero-EIP': {
      desktopBottomPadding: `${6 * theme.spacingMultiplier}px`,
      mobileBottomPadding: `${4 * theme.spacingMultiplier}px`
    },
    'isc-hero-full-screen-large-text': {
      desktopBottomPadding: `calc(50% - ${6 * theme.spacingMultiplier}px)`,
      mobileBottomPadding: `calc(50% - ${4 * theme.spacingMultiplier}px)`
    },
    'isc-hero-loyalty': {
      desktopBottomPadding: `${3 * theme.spacingMultiplier}px`,
      mobileBottomPadding: `${3 * theme.spacingMultiplier}px`
    },
  }
}

const getPaddingBottom = (theme, centerTextAlignment, isTitleBanner) => {
  if (isTitleBanner) {
    return getEmptyPadding(theme)
  } else if (centerTextAlignment) {
    return getCenterTextAlignmentPadding(theme)
  }

  return {
    'isc-hero': { desktopBottomPadding: `${5 * theme.spacingMultiplier}px`, mobileBottomPadding: `${5 * theme.spacingMultiplier}px` },
    'isc-hero-mini': { desktopBottomPadding: `${5 * theme.spacingMultiplier}px`, mobileBottomPadding: `${5 * theme.spacingMultiplier}px` },
    'isc-hero-large': { desktopBottomPadding: `${6 * theme.spacingMultiplier}px`, mobileBottomPadding: `${4 * theme.spacingMultiplier}px` },
    'isc-hero-title-banner': { desktopBottomPadding: `${6 * theme.spacingMultiplier}px`, mobileBottomPadding: `${4 * theme.spacingMultiplier}px` },
    'isc-hero-full-screen': { desktopBottomPadding: `${6 * theme.spacingMultiplier}px`, mobileBottomPadding: `${4 * theme.spacingMultiplier}px` },
    'isc-hero-EIP': { desktopBottomPadding: `${6 * theme.spacingMultiplier}px`, mobileBottomPadding: `${4 * theme.spacingMultiplier}px` },
    'isc-hero-full-screen-large-text': { desktopBottomPadding: `${6 * theme.spacingMultiplier}px`, mobileBottomPadding: `${4 * theme.spacingMultiplier}px` },
    'isc-hero-loyalty': {
      desktopBottomPadding: `${3 * theme.spacingMultiplier}px`,
      mobileBottomPadding: `${3 * theme.spacingMultiplier}px`
    },
  }
}

const generateIsStickyStyles = (isTitleBanner) => {
  if (!isTitleBanner) {
    return {}
  }

  return {
    additionalStyles: { position: 'sticky', top: '50% ' },
    additionalOuterStyles: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }
  }
}

class CMHero extends PureComponent {
  static layoutVariant = [
    'isc-hero',
    'isc-hero-mini',
    'isc-hero-large',
    'isc-hero-title-banner',
    'isc-hero-full-screen',
    'isc-hero-EIP',
    'isc-hero-full-screen-large-text',
    'isc-hero-loyalty'
  ]

  render() {
    const { item, theme, config, imageProps, ...rest } = this.props
    const { tags, pictures, ...itemRest } = item
    const { mobile, desktop, xlDesktop } = layoutHeightMap[item.layoutVariant]

    const isFullScreenHeroLargeText = item.layoutVariant === 'isc-hero-full-screen-large-text'
    const bottomAlignment = this.props.bottomAlignment || hasTag(tags, 'Hero.bottomAlignment') ? 'bottom' : undefined
    const topAlignment = this.props.topAlignment || hasTag(tags, 'Hero.topAlignment') ? 'top' : undefined
    const centerTextAlignment = this.props.centerTextAlignment || hasTag(tags, 'Hero.centerTextAlignment') || isFullScreenHeroLargeText
    const gridSpanOffset = calculateGridOffset(centerTextAlignment, theme.direction)
    const isTitleBanner = item.layoutVariant === 'isc-hero-title-banner'
    const dataCmMetadata = item.dataCmMetadata
    const { desktopBottomPadding, mobileBottomPadding } = getPaddingBottom(theme, centerTextAlignment, isTitleBanner)[item.layoutVariant]
    const additionalStyles = centerTextAlignment ? { marginLeft: '0' } : {}
    const isLoyaltyHeader = item.layoutVariant === 'isc-hero-EIP'
    const isNewLoyaltyHeader = item.layoutVariant === 'isc-hero-loyalty'

    const TextComponent = () => {
      if (isLoyaltyHeader) {
        return <LoyaltyHeadingComponent teaserTitle={item.teaserTitle} preTitle={item.preTitle} teaserText={item.teaserText} theme={theme} />
      }
      if (isNewLoyaltyHeader){
        return <NewLoyaltyHeadingComponent teaserTitle={item.teaserTitle} preTitle={item.preTitle} teaserText={item.teaserText} theme={theme} />
      }
      return (
        <Block
          centerTextAlignment={centerTextAlignment}
          inverseColor
          item={{
            ...itemRest,
            layoutVariant: isTitleBanner || isFullScreenHeroLargeText ? 'isc-block-with-jumbo-title' : 'isc-block-with-xlarge-title'
          }}
          {...rest}
        />
      )
    }

    return (
      <Hero
        data-automation="cm-hero"
        dataCmMetadata={dataCmMetadata}
        mobileHeight={mobile}
        desktopHeight={desktop}
        xlDesktopHeight={xlDesktop}
        mobileBottomPadding={mobileBottomPadding}
        desktopBottomPadding={desktopBottomPadding}
        config={config}
        opacity="0.1"
        isTitleBanner={isTitleBanner}
        verticalAlignment={bottomAlignment || topAlignment}
        imageProps={imageProps}
        isLoyaltyHeader={isLoyaltyHeader}
        isNewLoyaltyHeader={isNewLoyaltyHeader}
        foreground={
          <ThemeProvider theme={{ ...theme, direction: 'ltr' }}>
            <Grid.Row {...generateIsStickyStyles(isTitleBanner)} fullBleed={isLoyaltyHeader || isNewLoyaltyHeader}>
              <Grid.Span small={gridSpanOffset.small} medium={gridSpanOffset.medium} additionalStyles={additionalStyles} fullBleed={isLoyaltyHeader || isNewLoyaltyHeader}>
                <ThemeProvider theme={theme}>
                  <TextComponent />
                </ThemeProvider>
              </Grid.Span>
            </Grid.Row>
          </ThemeProvider>
        }
        pictures={pictures}
      />
    )
  }
}

CMHero.propTypes = {
  item: shape({
    subTitlePlain: string,
    bottomAlignment: string,
    topAlignment: string,
    centreTextAlignment: bool,
    pictures: arrayOf(
      shape({
        imageUrl: string.isRequired
      })
    ).isRequired
  }).isRequired,
  config: shape({
    imagesphere: shape({
      quality: number
    })
  }),
  imageProps: shape({
    lazy: bool
  })
}

CMHero.defaultProps = {
  config: {},
  imageProps: {
    lazy: true
  }
}

export default withTheme(CMHero)